import { gql } from "@apollo/client";
import { Button } from "@chakra-ui/button";
import { FormHelperText, FormLabel } from "@chakra-ui/form-control";
import { Divider, Flex, Heading, Spacer, Text } from "@chakra-ui/layout";
import { Tag } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { FormControl } from "formik-chakra-ui";
import { useCallback, useMemo } from "react";
import { InputWithTags } from "src/components/Inputs/InputWithTags";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { AdminPage } from "src/components/Layout/AdminPage";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { FORM_STATUS, OFFER_STATUS, WAITLIST_STATUS } from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useUserProfile } from "src/hooks/useUserProfile";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import * as GQL from "src/types/graphql";
import { z } from "zod";

type StatusOption = {
  label: string;
  id: GQL.form_status_enum;
};

type SubStatusOption = {
  label: string;
  id: GQL.offer_status_enum | GQL.waitlist_status_enum;
};

export const UserPreferencesSchema = z.object({
  statusId: z.string().optional(),
  statusValues: z
    .object({
      label: z.string(),
      id: z.string(),
    })
    .array(),
  subStatusId: z.string().optional(),
  subStatusValues: z
    .object({
      label: z.string(),
      id: z.string(),
    })
    .array(),
  forwardEmails: z.string().array(),
});

export type UserPreferencesForm = z.infer<typeof UserPreferencesSchema>;

export const Notifications = () => {
  const organization = useOrganization();
  const profile = useUserProfile();
  const toast = useAvelaToast();
  const { remoteData, refetch } = useRemoteDataQuery<
    GQL.GetUserPreferences,
    GQL.GetUserPreferencesVariables
  >(
    gql`
      query GetUserPreferences($personId: uuid!) {
        user_preference(where: { person_id: { _eq: $personId } }) {
          id
          type
          resource
          values
        }
      }
    `,
    {
      variables: {
        personId: profile.toNullable()?.id ?? "",
      },
      skip: !profile.hasData(),
    }
  );

  const [updateUserPreferences] = useRemoteDataMutation<
    GQL.UpdateUserPreferences,
    GQL.UpdateUserPreferencesVariables
  >(gql`
    mutation UpdateUserPreferences(
      $id: uuid!
      $preferences: user_preference_set_input!
    ) {
      update_user_preference_by_pk(
        pk_columns: { id: $id }
        _set: $preferences
      ) {
        id
      }
    }
  `);

  const [createUserPreferences] = useRemoteDataMutation<
    GQL.CreateUserPreferences,
    GQL.CreateUserPreferencesVariables
  >(gql`
    mutation CreateUserPreferences(
      $preferences: user_preference_insert_input!
    ) {
      insert_user_preference_one(object: $preferences) {
        id
      }
    }
  `);

  const statusOptions: StatusOption[] = useMemo(() => {
    return [
      {
        label: FORM_STATUS[GQL.form_status_enum.Submitted].label,
        id: GQL.form_status_enum.Submitted,
      },
    ];
  }, []);

  const subStatusOptions: SubStatusOption[] = useMemo(() => {
    return [
      {
        label: OFFER_STATUS[GQL.offer_status_enum.Accepted].label,
        id: GQL.offer_status_enum.Accepted,
      },
      {
        label: OFFER_STATUS[GQL.offer_status_enum.Declined].label,
        id: GQL.offer_status_enum.Declined,
      },
      {
        label: WAITLIST_STATUS[GQL.waitlist_status_enum.Withdrawn].label,
        id: GQL.waitlist_status_enum.Withdrawn,
      },
    ];
  }, []);

  const handleSubmit = useCallback(
    async (values: UserPreferencesForm) => {
      try {
        if (values.statusId) {
          await updateUserPreferences({
            variables: {
              id: values.statusId,
              preferences: {
                values: {
                  status: values.statusValues.map((value) => value.id),
                  forwardEmails: values.forwardEmails,
                },
              },
            },
          });
        } else {
          if (!profile.hasData()) {
            throw new Error("Missing user profile");
          }
          await createUserPreferences({
            variables: {
              preferences: {
                person_id: profile.data.id,
                type: GQL.user_preference_type_enum.Email,
                resource: GQL.user_preference_resource_enum.Status,
                values: {
                  status: values.statusValues.map((value) => value.id),
                  forwardEmails: values.forwardEmails,
                },
              },
            },
          });
        }

        if (values.subStatusId) {
          await updateUserPreferences({
            variables: {
              id: values.subStatusId,
              preferences: {
                values: {
                  status: values.subStatusValues.map((value) => value.id),
                  forwardEmails: values.forwardEmails,
                },
              },
            },
          });
        } else {
          if (!profile.hasData()) {
            throw new Error("Missing user profile");
          }
          await createUserPreferences({
            variables: {
              preferences: {
                person_id: profile.data.id,
                type: GQL.user_preference_type_enum.Email,
                resource: GQL.user_preference_resource_enum.SubStatus,
                values: {
                  status: values.subStatusValues.map((value) => value.id),
                  forwardEmails: values.forwardEmails,
                },
              },
            },
          });
        }

        await refetch();
        toast({
          title: "Preferences updated",
          description: "Your preferences have been updated",
          status: "success",
        });
      } catch (error) {
        console.error(error);
        toast({
          title: "Error updating preferences",
          description: "Please try again",
          status: "error",
        });
      }
    },
    [createUserPreferences, refetch, toast, updateUserPreferences, profile]
  );

  return (
    <AdminPage
      breadcrumb={
        <Breadcrumb
          items={breadcrumb.userProfile.getBreadcrumbsForNotifications(
            organization
          )}
        />
      }
      heading={
        <Heading as="h1" variant="admin">
          Notifications
        </Heading>
      }
    >
      <GQLRemoteDataView remoteData={remoteData}>
        {(data) => {
          const userPreferenceStatus = data.user_preference.find(
            (userPreference) =>
              userPreference.resource ===
              GQL.user_preference_resource_enum.Status
          );
          const userPreferenceSubStatus = data.user_preference.find(
            (userPreference) =>
              userPreference.resource ===
              GQL.user_preference_resource_enum.SubStatus
          );

          const initialValues = {
            statusId: userPreferenceStatus?.id || "",
            statusValues:
              userPreferenceStatus?.values?.status.map(
                (statusValue: string) => ({
                  label: statusOptions.find(
                    (option) => option.id === statusValue
                  )?.label,
                  id: statusValue,
                })
              ) || [],
            subStatusId: userPreferenceSubStatus?.id || "",
            subStatusValues:
              userPreferenceSubStatus?.values?.status?.map(
                (subStatusValue: string) => ({
                  label: subStatusOptions.find(
                    (option) => option.id === subStatusValue
                  )?.label,
                  id: subStatusValue,
                })
              ) || [],
            forwardEmails: userPreferenceStatus?.values?.forwardEmails || [],
          };

          return (
            <Formik<UserPreferencesForm>
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validate={validateWithZod(UserPreferencesSchema)}
              enableReinitialize
            >
              {({ values, setFieldValue }) => {
                return (
                  <Flex as={Form} direction="column" gap={4} h="100%" grow="1">
                    <Flex direction="column">
                      <Text fontSize="lg" color="gray.800">
                        Email
                      </Text>
                      <Text>
                        Choose what you want to receive notifications about over
                        email. This will only be applied to you, and any
                        forwarding email addresses you set up.
                      </Text>
                    </Flex>

                    <Flex direction="column" gap={2}>
                      <Flex direction="column">
                        <Text>Form responses moved to a specific status</Text>
                        <Text fontSize="xs" color="gray.600" fontWeight="400">
                          Form responses that have recently moved to the
                          following status:
                        </Text>
                      </Flex>

                      <FormControl name="statusValues" w="50%">
                        <FormLabel>
                          Status
                          <MultiSelectInput
                            size="md"
                            onChange={(values) =>
                              setFieldValue("statusValues", values)
                            }
                            options={statusOptions}
                            initialValues={values.statusValues}
                            renderDropdownItem={(item) => {
                              return <Text>{item.label}</Text>;
                            }}
                            labelAttr="label"
                          />
                        </FormLabel>
                        <FormHelperText>
                          Select one or more statuses to be notified about
                        </FormHelperText>
                      </FormControl>
                    </Flex>

                    <Divider />
                    <Flex direction="column" gap={2}>
                      <Tag
                        size="sm"
                        variant="solid"
                        color="blackAlpha.900"
                        bg="gray.200"
                        width="fit-content"
                        fontSize="xs"
                      >
                        Lottery & offers
                      </Tag>
                      <Flex direction="column">
                        <Text>
                          Form responses moved to a specific sub-status{" "}
                        </Text>
                        <Text fontSize="xs" color="gray.600" fontWeight="400">
                          Form responses that have recently moved to the
                          following sub-status:
                        </Text>
                      </Flex>

                      <FormControl name="subStatusValues" w="50%">
                        <FormLabel>
                          Sub-status
                          <MultiSelectInput
                            size="md"
                            onChange={(values) =>
                              setFieldValue("subStatusValues", values)
                            }
                            options={subStatusOptions}
                            initialValues={values.subStatusValues}
                            renderDropdownItem={(item) => {
                              return <Text>{item.label}</Text>;
                            }}
                            labelAttr="label"
                          />
                        </FormLabel>
                        <FormHelperText>
                          Select one or more sub-statuses to be notified about
                        </FormHelperText>
                      </FormControl>
                    </Flex>

                    <Divider />
                    <InputWithTags
                      initialValues={values.forwardEmails}
                      onChange={(values) => {
                        setFieldValue("forwardEmails", values);
                      }}
                      label="Forwarding addresses"
                      inputProps={{
                        placeholder:
                          "Enter email addresses separated by commas (,)",
                        width: "50%",
                      }}
                      addButtonProps={{
                        flexShrink: "0",
                        children: "Add addresses",
                      }}
                      helperText="Forward notifications you enable to any address"
                    />

                    <Spacer />
                    <AdminFormButtons>
                      <Button type="submit">Update</Button>
                    </AdminFormButtons>
                  </Flex>
                );
              }}
            </Formik>
          );
        }}
      </GQLRemoteDataView>
    </AdminPage>
  );
};
